/**
 * Platform Billing Dashboard
 * Seat usage, staff management, plan summary, add seats, billing contact.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { enhancedApi } from '../api';
import AddUserPage from '../components/AddUserPage';
import {
  Box,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  LinearProgress,
  TextField,
  Skeleton,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import CheckIcon from '@mui/icons-material/Check';
import PeopleIcon from '@mui/icons-material/People';
import EmailIcon from '@mui/icons-material/Email';

const PlatformBilling = () => {
  const navigate = useNavigate();
  const [seatInfo, setSeatInfo] = useState(null);
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addingSeats, setAddingSeats] = useState(false);
  const [deactivatingId, setDeactivatingId] = useState(null);
  const [addSeatQty, setAddSeatQty] = useState(1);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [deactivateUserId, setDeactivateUserId] = useState(null);
  const [deactivateUserName, setDeactivateUserName] = useState('');

  const fetchSeats = async () => {
    try {
      const res = await enhancedApi.getSeatInfo();
      const data = res.data?.data ?? res.data;
      setSeatInfo(data);
    } catch {
      setError('Failed to load seat information');
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await enhancedApi.getUsers();
      const data = res.data?.results ?? res.data?.data ?? res.data;
      const list = Array.isArray(data) ? data : (data?.users ?? []);
      setUsers(list);
    } catch {
      setError('Failed to load staff list');
    }
  };

  const fetchPlans = async () => {
    try {
      const res = await enhancedApi.getPlatformPlans();
      const fetched = res.data?.plans ?? res.data;
      setPlans(Array.isArray(fetched) ? fetched : []);
    } catch {
      setPlans([]);
    }
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    await Promise.all([fetchSeats(), fetchUsers(), fetchPlans()]);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const handleAddSeats = async () => {
    const qty = Math.max(1, parseInt(addSeatQty, 10) || 1);
    setAddingSeats(true);
    setError(null);
    try {
      await enhancedApi.addSeats(qty);
      setAddSeatQty(1);
      await fetchSeats();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add seats');
    } finally {
      setAddingSeats(false);
    }
  };

  const handleDeactivate = async (userId) => {
    setDeactivatingId(userId);
    setError(null);
    try {
      await enhancedApi.updateUser(userId, { is_active: false });
      setDeactivateUserId(null);
      setDeactivateUserName('');
      await Promise.all([fetchSeats(), fetchUsers()]);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to deactivate user');
    } finally {
      setDeactivatingId(null);
    }
  };

  const handleInviteSuccess = () => {
    setInviteOpen(false);
    fetchUsers();
    fetchSeats();
  };

  const used = seatInfo?.used ?? 0;
  const limit = seatInfo?.limit ?? 0;
  const pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
  const isUnlimited = seatInfo?.unlimited === true;
  const planKey = seatInfo?.plan || 'pro';
  const planName = plans.find((p) => p.key === planKey)?.name || (planKey.charAt(0).toUpperCase() + planKey.slice(1));
  const available = isUnlimited || limit === 0 ? '—' : Math.max(0, limit - used);

  const getProgressColor = () => {
    if (isUnlimited || limit === 0) return 'success.main';
    if (pct >= 100) return 'error.main';
    if (pct >= 80) return 'warning.main';
    return 'success.main';
  };

  const showWarning = !isUnlimited && limit > 0 && pct >= 80;

  const staffColumns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: 'primary.main',
              fontSize: '0.875rem',
            }}
          >
            {(params.row.first_name?.[0] || params.row.name?.[0] || '?').toUpperCase()}
          </Avatar>
          <Typography variant="body2">
            {params.row.first_name || params.row.name || '—'} {params.row.last_name || ''}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 200,
      valueGetter: (_, row) => row.email || '—',
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 140,
      valueGetter: (_, row) => row.role?.name || row.role || '—',
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.row.is_active !== false ? 'Active' : 'Inactive'}
          size="small"
          color={params.row.is_active !== false ? 'success' : 'default'}
          sx={
            params.row.is_active === false
              ? { bgcolor: 'grey.400', color: 'grey.900' }
              : undefined
          }
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) =>
        params.row.is_active !== false ? (
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<PersonOffIcon />}
            onClick={(e) => {
              e.stopPropagation();
              const name = `${params.row.first_name || params.row.name || ''} ${params.row.last_name || ''}`.trim() || 'this user';
              setDeactivateUserName(name);
              setDeactivateUserId(params.row.id);
            }}
            disabled={deactivatingId === params.row.id}
          >
            Deactivate
          </Button>
        ) : null,
    },
  ];

  const currentPlan = plans.find((p) => p.key === planKey);
  const planFeatures = currentPlan?.features ?? [];

  if (loading) {
    return (
      <Box sx={{ width: '100%' }}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <Skeleton variant="rounded" height={180} sx={{ mb: 3 }} />
            <Skeleton variant="rounded" height={320} />
          </Grid>
          <Grid item xs={12} lg={4}>
            <Skeleton variant="rounded" height={200} sx={{ mb: 2 }} />
            <Skeleton variant="rounded" height={180} sx={{ mb: 2 }} />
            <Skeleton variant="rounded" height={120} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <React.Fragment>
      <Box sx={{ width: '100%' }}>
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            onClose={() => setError(null)}
            action={
              <Button color="inherit" size="small" onClick={load}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Left column */}
          <Grid item xs={12} lg={8}>
            {/* Seat Usage Card */}
            <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h4" fontWeight={700} gutterBottom>
                  {isUnlimited || limit === 0
                    ? `${used} seats used`
                    : `${used} / ${limit} Seats Used`}
                </Typography>
                {!isUnlimited && limit > 0 && (
                  <>
                    <LinearProgress
                      variant="determinate"
                      value={pct}
                      sx={{
                        height: 12,
                        borderRadius: 2,
                        mb: 2,
                        bgcolor: 'grey.200',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: getProgressColor(),
                          borderRadius: 2,
                        },
                      }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {used} seats used · {available} seats available · Plan: {planName}
                    </Typography>
                    {showWarning && (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        You're almost at your seat limit. Add more seats to avoid disruption.
                      </Alert>
                    )}
                  </>
                )}
                {(isUnlimited || limit === 0) && (
                  <Typography variant="body2" color="text.secondary">
                    {isUnlimited ? 'Unlimited seats' : 'No seat limit configured'}
                  </Typography>
                )}
              </CardContent>
            </Card>

            {/* Staff Table Card */}
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" fontWeight={600}>
                    Team Members
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setInviteOpen(true)}
                  >
                    Invite Team Member
                  </Button>
                </Box>
                {users.length === 0 ? (
                  <Box
                    sx={{
                      py: 6,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 2,
                      color: 'text.secondary',
                    }}
                  >
                    <PeopleIcon sx={{ fontSize: 48, opacity: 0.5 }} />
                    <Typography>No team members yet</Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => setInviteOpen(true)}
                    >
                      Invite Team Member
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ height: 400 }}>
                    <DataGrid
                      rows={users}
                      columns={staffColumns}
                      getRowId={(row) => row.id}
                      initialState={{
                        pagination: { paginationModel: { pageSize: 10 } },
                      }}
                      pageSizeOptions={[10, 25, 50]}
                      disableRowSelectionOnClick
                      sx={{
                        border: 'none',
                        '& .MuiDataGrid-cell': { borderColor: 'divider' },
                        '& .MuiDataGrid-columnHeaders': { bgcolor: 'action.hover' },
                      }}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Right column */}
          <Grid item xs={12} lg={4}>
            {/* Plan Summary Card */}
            <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Current Plan
                </Typography>
                <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                  {planName}
                </Typography>
                {planFeatures.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    {planFeatures.map((f, i) => (
                      <Box
                        key={i}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          py: 0.5,
                        }}
                      >
                        <CheckIcon sx={{ fontSize: 18, color: 'success.main' }} />
                        <Typography variant="body2">{f}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
                <Button variant="contained" fullWidth sx={{ mb: 1 }}>
                  Upgrade Plan
                </Button>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => navigate('/billing/invoices')}
                  sx={{ display: 'block', textAlign: 'center', mt: 1 }}
                >
                  View Invoice History
                </Link>
              </CardContent>
            </Card>

            {/* Add Seats Card */}
            <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Need more seats?
                </Typography>
                <TextField
                  type="number"
                  fullWidth
                  size="small"
                  value={addSeatQty}
                  onChange={(e) => setAddSeatQty(Math.max(1, parseInt(e.target.value, 10) || 1))}
                  inputProps={{ min: 1 }}
                  sx={{ my: 2 }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Price per seat: Contact us
                </Typography>
                <Button
                  variant="contained"
                  color="success"
                  fullWidth
                  onClick={handleAddSeats}
                  disabled={addingSeats || isUnlimited || limit === 0}
                >
                  {addingSeats ? 'Adding…' : 'Add Seats'}
                </Button>
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 2 }}>
                  Seats are billed monthly, prorated
                </Typography>
              </CardContent>
            </Card>

            {/* Billing Contact Card */}
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Billing Questions?
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <EmailIcon fontSize="small" color="action" />
                  <Link href="mailto:billing@beck.app" underline="hover" color="primary">
                    billing@beck.app
                  </Link>
                </Box>
                <Link
                  href="https://beck.app/docs"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body2"
                  underline="hover"
                  color="primary"
                >
                  Documentation
                </Link>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} fullWidth maxWidth="sm">
        <AddUserPage
          isDialog={true}
          onInviteSubmit={handleInviteSuccess}
          onClose={() => setInviteOpen(false)}
        />
      </Dialog>

      {/* Deactivate confirmation */}
      <Dialog
        open={!!deactivateUserId}
        onClose={() => {
          setDeactivateUserId(null);
          setDeactivateUserName('');
        }}
      >
        <DialogTitle>Deactivate team member?</DialogTitle>
        <DialogContent>
          <Typography>
            Deactivate {deactivateUserName || 'this user'}? This will free a seat.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeactivateUserId(null);
              setDeactivateUserName('');
            }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deactivateUserId && handleDeactivate(deactivateUserId)}
            disabled={deactivatingId === deactivateUserId}
          >
            {deactivatingId === deactivateUserId ? 'Deactivating…' : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default PlatformBilling;
